import { Controller } from "@hotwired/stimulus"
import { getStorage } from "../instrument_take/helpers/storage.js"
import { useEscapeKey } from "../helpers/escape_key_mixin"
import { visibleSteps } from "../helpers/interface_tour_steps"

const COOKIE_CONSENT_STORAGE_KEY = "cookie_consent_accepted"
const COOKIE_CONSENT_EVENT = "cookie-consent:accepted"
const START_EVENT = "interface-tour:start"
const SPOTLIGHT_PADDING = 8
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled]):not([type=\"hidden\"])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex=\"-1\"])"
].join(", ")

export default class extends Controller {
  static targets = ["overlay", "spotlight", "card", "progress", "title", "body", "back", "skip", "next", "done"]
  static values = {
    autoplay: { type: Boolean, default: false },
    completeUrl: String,
    progressTemplate: String,
    steps: Array
  }

  connect() {
    this.index = 0
    this.visible = []
    this.persistStarted = false
    this._active = false
    this._escapeKey = useEscapeKey(this, this.closeOnEscape)
    this._onReposition = () => this.positionSpotlight()
    this._trapTab = (event) => this.trapTabKey(event)
    this._cookieAccepted = this.onCookieAccepted.bind(this)
    this._startRequested = this.onStartRequested.bind(this)

    document.addEventListener(START_EVENT, this._startRequested)
    if (this.autoplayValue) this.requestStart()
  }

  disconnect() {
    document.removeEventListener(START_EVENT, this._startRequested)
    document.removeEventListener(COOKIE_CONSENT_EVENT, this._cookieAccepted)
    this.teardown()
  }

  onStartRequested() {
    this.requestStart()
  }

  next() {
    this.persistCompletion()
    if (this.index >= this.visible.length - 1) {
      this.finish()
      return
    }

    this.index += 1
    this.renderStep()
  }

  back() {
    this.persistCompletion()
    if (this.index === 0) return

    this.index -= 1
    this.renderStep()
  }

  skip(event) {
    event?.preventDefault()
    this.persistCompletion()
    this.close()
  }

  finish(event) {
    event?.preventDefault()
    this.persistCompletion()
    this.close()
  }

  closeOnEscape(event) {
    if (event.key !== "Escape") return
    this.skip(event)
  }

  requestStart() {
    if (this.canStart()) {
      this.start()
      return
    }

    document.addEventListener(COOKIE_CONSENT_EVENT, this._cookieAccepted)
  }

  start() {
    this.visible = visibleSteps(this.stepsValue, document)
    if (this.visible.length === 0) return

    this.index = 0
    this.showUi()
    if (!this._active) {
      this._tornDown = false
      this._active = true
      document.body.classList.add("is-clipped")
      this._escapeKey.add()
      this.cardTarget.addEventListener("keydown", this._trapTab)
      window.addEventListener("resize", this._onReposition)
      window.addEventListener("scroll", this._onReposition, true)
    }
    this.renderStep()
  }

  canStart() {
    if (getStorage(COOKIE_CONSENT_STORAGE_KEY)) return true
    return !document.querySelector(".cookie-consent-banner")
  }

  onCookieAccepted() {
    document.removeEventListener(COOKIE_CONSENT_EVENT, this._cookieAccepted)
    this.start()
  }

  renderStep() {
    const step = this.visible[this.index]
    if (!step) return

    const total = this.visible.length
    this.progressTarget.textContent = this.progressTemplateValue
      .replace("{{current}}", String(this.index + 1))
      .replace("{{total}}", String(total))
    this.titleTarget.textContent = step.title
    this.bodyTarget.textContent = step.body

    const last = this.index === total - 1
    this.backTarget.hidden = this.index === 0
    this.nextTarget.hidden = last
    this.doneTarget.hidden = !last
    this.skipTarget.hidden = last

    this.reveal(step)
    this.positionSpotlight()
    this.focusPrimary()
  }

  reveal(step) {
    this.restoreReveals()
    if (step.reveal === "sidebar") this.openSidebar()
    if (step.reveal === "navbar") this.openNavbar()
  }

  openSidebar() {
    const details = document.querySelector("#sidebar-menu details.app-sidebar-mobile")
    if (!details) return
    if (!window.matchMedia("(max-width: 768px)").matches) return
    if (details.open) return

    details.open = true
    this._openedSidebar = details
  }

  openNavbar() {
    if (!window.matchMedia("(max-width: 1023px)").matches) return

    const menu = document.getElementById("navbar-main-menu")
    const burger = document.getElementById("navbar-burger-toggle")
    if (!menu || menu.classList.contains("is-active")) return

    menu.classList.add("is-active")
    burger?.classList.add("is-active")
    burger?.setAttribute("aria-expanded", "true")
    this._openedNavbar = { menu, burger }
  }

  restoreReveals() {
    if (this._openedSidebar) {
      this._openedSidebar.open = false
      this._openedSidebar = null
    }
    if (this._openedNavbar) {
      this._openedNavbar.menu.classList.remove("is-active")
      this._openedNavbar.burger?.classList.remove("is-active")
      this._openedNavbar.burger?.setAttribute("aria-expanded", "false")
      this._openedNavbar = null
    }
  }

  positionSpotlight() {
    const step = this.visible[this.index]
    const target = step?.selector ? document.querySelector(step.selector) : null
    if (!target) {
      this.spotlightTarget.hidden = true
      this.overlayTarget.classList.remove("is-cutout")
      return
    }

    target.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "instant" })
    const rect = target.getBoundingClientRect()
    this.spotlightTarget.hidden = false
    this.overlayTarget.classList.add("is-cutout")
    this.spotlightTarget.style.top = `${Math.max(rect.top - SPOTLIGHT_PADDING, 0)}px`
    this.spotlightTarget.style.left = `${Math.max(rect.left - SPOTLIGHT_PADDING, 0)}px`
    this.spotlightTarget.style.width = `${rect.width + SPOTLIGHT_PADDING * 2}px`
    this.spotlightTarget.style.height = `${rect.height + SPOTLIGHT_PADDING * 2}px`
  }

  focusPrimary() {
    const last = this.index === this.visible.length - 1
    const button = last ? this.doneTarget : this.nextTarget
    window.requestAnimationFrame(() => button.focus({ preventScroll: true }))
  }

  focusableElements() {
    return [...this.cardTarget.querySelectorAll(FOCUSABLE_SELECTOR)].filter((el) => {
      if (!(el instanceof HTMLElement) || el.hidden) return false
      if (el.hasAttribute("disabled") || el.getAttribute("aria-hidden") === "true") return false
      if (typeof el.checkVisibility === "function") {
        return el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })
      }
      const style = window.getComputedStyle(el)
      return style.visibility !== "hidden" && style.display !== "none"
    })
  }

  trapTabKey(event) {
    if (event.key !== "Tab") return
    const focusables = this.focusableElements()
    if (focusables.length === 0) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault()
        last.focus()
      }
    } else if (document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  persistCompletion() {
    if (this.persistStarted || !this.completeUrlValue) return

    this.persistStarted = true
    const token = document.querySelector("meta[name='csrf-token']")?.content
    fetch(this.completeUrlValue, {
      method: "PATCH",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": token || "",
        Accept: "application/json"
      }
    }).then((response) => {
      if (!response.ok) throw new Error("tour persist failed")
    }).catch(() => {
      this.persistStarted = false
    })
  }

  close() {
    this.teardown()
    this.hideUi()
  }

  showUi() {
    this.element.hidden = false
    this.overlayTarget.hidden = false
    this.cardTarget.hidden = false
  }

  hideUi() {
    this.element.hidden = true
    this.overlayTarget.hidden = true
    this.spotlightTarget.hidden = true
    this.cardTarget.hidden = true
    this.overlayTarget.classList.remove("is-cutout")
  }

  teardown() {
    if (this._tornDown) return

    this._tornDown = true
    this._active = false
    this._escapeKey?.remove()
    if (this.hasCardTarget) this.cardTarget.removeEventListener("keydown", this._trapTab)
    window.removeEventListener("resize", this._onReposition)
    window.removeEventListener("scroll", this._onReposition, true)
    document.body.classList.remove("is-clipped")
    this.restoreReveals()
  }
}
